/** Compare terminal flow catalog vs runtime step builders. */
import { TERMINAL_FLOWS, listCatalogSteps } from './terminal-flow-catalog.js';
import { initNewTicketDataV2, buildNewTicketStepsV2 } from './assistant-ticket-v2.js';
import { initVisaData, buildVisaSteps } from './assistant-visa-new.js';
import { initTourData, buildTourSteps } from './assistant-tour-new.js';
import { ticketStageSteps } from './assistant-ticket-stages.js';
import { visaStageSteps } from './assistant-visa-stages.js';
import { tourStageSteps } from './assistant-tour-stages.js';

const DEFAULT_SETTINGS = {
  sources: ['Call', 'WhatsApp'],
  visas: ['India eVisa', 'Malaysia eVisa'],
  visa_costs: { 'India eVisa': 18000 },
  tours: ['Vietnam', 'India'],
};

function wildcardToRegExp(pattern) {
  const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`^${escaped.replace(/\\\*/g, '[^:]+')}$`);
}

function catalogMatchesRuntimeKey(flow, runtimeKey) {
  return listCatalogSteps(flow).some((s) => {
    if (s.pattern) return wildcardToRegExp(s.key).test(runtimeKey);
    return s.key === runtimeKey;
  });
}

function buildHelpers(settings = DEFAULT_SETTINGS) {
  const sources = settings.sources || DEFAULT_SETTINGS.sources;
  return {
    sources,
    matchSource: (v) => v,
    formatSourceQuestion: () => 'Lead source?',
    normalizePhone: (v) => v,
    defaultFollowupISO: () => '2026-07-25',
    visaPackages: settings.visas || DEFAULT_SETTINGS.visas,
    visaCosts: settings.visa_costs || DEFAULT_SETTINGS.visa_costs,
    tourPackages: settings.tours || DEFAULT_SETTINGS.tours,
  };
}

function mockLead(flow) {
  return {
    id: 'drift-lead',
    name: 'Test Lead',
    phone: '+94 77 123 4567',
    type: flow.type,
    status: flow.status,
    amount: 500000,
    cost_amount: 400000,
    extra: {
      pax: { adt: 2, chd: 1, inf: 0, stu: 0 },
      pax_rates: { adt: 25000 },
      pax_cost_rates: { adt: 18000 },
      quoted_total: 500000,
      stage_data: { quotation: { total: 580000, sell_total: 580000, cost_total: 450000 } },
    },
  };
}

function collectNewFlowKeys(flow, helpers) {
  const keys = new Set();
  const add = (data) => {
    let steps = [];
    if (flow.type === 'visa') steps = buildVisaSteps(data, helpers);
    else if (flow.type === 'tour') steps = buildTourSteps(data, helpers).filter((s) => !s.when || s.when(data));
    else steps = buildNewTicketStepsV2(data, helpers).filter((s) => !s.when || s.when(data));
    steps.forEach((s) => keys.add(s.key));
  };

  if (flow.type === 'visa') {
    const data = initVisaData(helpers);
    add(data);
    data.package_name = 'India eVisa';
    add(data);
    data.pax.chd = 1;
    add(data);
  } else if (flow.type === 'tour') {
    const data = initTourData(helpers);
    add(data);
    data.pax = { adt: 2, chd: 1, inf: 0, stu: 0 };
    add(data);
    data.pax = { adt: 2, chd: 0, inf: 1, stu: 0 };
    add(data);
  } else {
    const base = initNewTicketDataV2();
    add(base);
    const transit = initNewTicketDataV2();
    transit.preferences.transit = 'transit';
    add(transit);
    const multi = initNewTicketDataV2();
    multi.preferences.transit = 'multi';
    add(multi);
    const ret = initNewTicketDataV2();
    ret.trip_type = 'return';
    add(ret);
  }
  return keys;
}

function collectStageFlowKeys(flow) {
  const keys = new Set();
  const sample = (lead, answers = {}) => {
    let steps = [];
    if (flow.type === 'visa') steps = visaStageSteps(flow.status, lead);
    else if (flow.type === 'tour') steps = tourStageSteps(flow.status, lead, answers);
    else steps = ticketStageSteps(flow.status, lead);
    steps.forEach((s) => keys.add(s.key));
  };

  sample(mockLead(flow));

  if (flow.type === 'ticket' && flow.status === 'quotation_sent') {
    const withInf = mockLead(flow);
    withInf.extra.pax = { adt: 2, chd: 1, inf: 1, stu: 0 };
    sample(withInf);
    const quick = mockLead(flow);
    quick.extra = { ...quick.extra, flow_version: 'ticket_v2', intake_details: 'DXB CMB return' };
    quick.origin = '';
    quick.destination = '';
    sample(quick);
  }

  if (flow.type === 'ticket' && flow.status === 'temporary_booking') {
    const quick = mockLead(flow);
    quick.extra = { ...quick.extra, flow_version: 'ticket_v2', intake_details: 'DXB CMB return' };
    quick.origin = '';
    quick.destination = '';
    sample(quick);
    const quickWithInf = mockLead(flow);
    quickWithInf.extra = {
      ...quickWithInf.extra,
      flow_version: 'ticket_v2',
      intake_details: 'DXB CMB return',
      pax: { adt: 2, chd: 1, inf: 1, stu: 0 },
    };
    quickWithInf.origin = '';
    quickWithInf.destination = '';
    sample(quickWithInf);
    sample(mockLead(flow));
  }

  if (flow.type === 'tour') {
    const withInf = mockLead(flow);
    withInf.extra.pax = { adt: 2, chd: 1, inf: 1, stu: 0 };
    withInf.extra.tour_quote_request = { ...(withInf.extra.tour_quote_request || {}), pax: withInf.extra.pax };
    sample(withInf);
    if (flow.status === 'quotation') {
      sample(withInf, { add_hotel_rooms: true });
    }
  }

  return keys;
}

function collectRuntimeKeys(flow, settings = DEFAULT_SETTINGS) {
  const helpers = buildHelpers(settings);
  if (flow.mode === 'new') return collectNewFlowKeys(flow, helpers);
  if (flow.mode === 'stage') return collectStageFlowKeys(flow);
  return new Set();
}

function validateOverrideKeys(overrides = {}) {
  const issues = [];
  const flowIds = new Set(TERMINAL_FLOWS.map((f) => f.id));
  for (const key of Object.keys(overrides)) {
    const dot = key.indexOf('.');
    if (dot < 1) {
      issues.push({ kind: 'bad_override_key', key, message: `Invalid override key: ${key}` });
      continue;
    }
    const flowId = key.slice(0, dot);
    const stepKey = key.slice(dot + 1);
    const flow = TERMINAL_FLOWS.find((f) => f.id === flowId);
    if (!flowIds.has(flowId)) {
      issues.push({ kind: 'unknown_flow', key, message: `Override references unknown flow: ${flowId}` });
      continue;
    }
    if (!catalogMatchesRuntimeKey(flow, stepKey) && !stepKey.includes('*')) {
      const covered = listCatalogSteps(flow).some((s) => s.pattern && wildcardToRegExp(s.key).test(stepKey));
      if (!covered) {
        issues.push({ kind: 'orphan_override', key, message: `Override key not in catalog: ${key}` });
      }
    }
    const val = overrides[key];
    if (!val || typeof val !== 'object') {
      issues.push({ kind: 'bad_override_value', key, message: `Override value must be an object: ${key}` });
    }
  }
  return issues;
}

/** @returns {{ ok: boolean, issues: Array, summary: { flows: number, overrides: number } }} */
export function checkTerminalFlowDrift(options = {}) {
  const overrides = options.overrides || {};
  const issues = [];
  const settings = options.settings || DEFAULT_SETTINGS;

  for (const flow of TERMINAL_FLOWS) {
    const catalogKeys = listCatalogSteps(flow).filter((s) => !s.pattern).map((s) => s.key);
    const runtimeKeys = collectRuntimeKeys(flow, settings);

    for (const rk of runtimeKeys) {
      if (!catalogMatchesRuntimeKey(flow, rk)) {
        issues.push({
          kind: 'missing_catalog',
          flowId: flow.id,
          stepKey: rk,
          message: `${flow.id}: runtime step "${rk}" missing from catalog`,
        });
      }
    }

    for (const ck of catalogKeys) {
      if (!runtimeKeys.has(ck)) {
        issues.push({
          kind: 'stale_catalog',
          flowId: flow.id,
          stepKey: ck,
          message: `${flow.id}: catalog step "${ck}" not found in sampled runtime paths`,
        });
      }
    }
  }

  issues.push(...validateOverrideKeys(overrides));

  return {
    ok: issues.length === 0,
    issues,
    summary: {
      flows: TERMINAL_FLOWS.length,
      overrides: Object.keys(overrides).length,
    },
  };
}

export function formatDriftReport(result) {
  if (result.ok) return 'Catalog drift check: OK';
  return result.issues.map((i) => i.message).join('\n');
}

const STALE_OVERRIDE_KINDS = new Set(['orphan_override', 'unknown_flow', 'bad_override_key', 'bad_override_value']);

/** Overrides pointing at unknown flows or step keys. */
export function findStaleOverrides(overrides = {}) {
  return validateOverrideKeys(overrides).filter((i) => STALE_OVERRIDE_KINDS.has(i.kind));
}

export function removeStaleOverrides(overrides = {}) {
  const stale = new Set(findStaleOverrides(overrides).map((i) => i.key));
  const next = { ...overrides };
  stale.forEach((k) => delete next[k]);
  return { next, removed: [...stale] };
}
